import React from 'react';
import { spacing, colors, borderRadius } from './tokens';

/**
 * AdminTable
 * 
 * Standardized table wrapper component for admin pages.
 * Provides consistent styling and spacing.
 * 
 * @param {ReactNode} children - Table content (thead, tbody)
 * @param {boolean} compact - Use compact spacing (reduced padding, smaller font)
 */
export default function AdminTable({ children, compact = false }) {
  return (
    <div className={`admin-table-container ${compact ? 'admin-table-compact' : ''}`}>
      <table className={`admin-table ${compact ? 'admin-table-compact' : ''}`}>
        {children}
      </table>
    </div>
  );
}

/**
 * AdminTableHeader
 * 
 * Standardized table header row.
 * Wraps children in <thead><tr>...</tr></thead>
 */
export function AdminTableHeader({ children }) {
  return (
    <thead>
      <tr>{children}</tr>
    </thead>
  );
}

/**
 * AdminTableRow
 * 
 * Standardized table row with hover state.
 */
export function AdminTableRow({ children, onClick, style = {}, className }) {
  return (
    <tr
      className={className}
      style={{
        ...styles.row,
        ...(onClick && styles.rowClickable),
        ...style,
      }}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

/**
 * AdminTableCell
 * 
 * Standardized table cell.
 * 
 * @param {ReactNode} children - Cell content
 * @param {object} style - Additional styles
 * @param {string} align - Text alignment (left, right, center)
 * @param {string} dataLabel - Label for mobile view (required for responsive tables)
 * @param {boolean} mobileHidden - Hide on mobile (default: false)
 */
export function AdminTableCell({ children, style = {}, align = 'left', dataLabel, mobileHidden = false }) {
  return (
    <td 
      style={{
        ...styles.cell,
        textAlign: align,
        ...style,
      }}
      data-label={dataLabel}
      data-mobile-hidden={mobileHidden ? 'true' : undefined}
    >
      {children}
    </td>
  );
}

/**
 * AdminTableHeaderCell
 * 
 * Standardized table header cell.
 * 
 * @param {ReactNode} children - Header content
 * @param {object} style - Additional styles
 * @param {string} align - Text alignment
 * @param {string} scope - ARIA scope (col, row)
 * @param {boolean} sortable - Whether column is sortable
 * @param {function} onSort - Sort handler
 * @param {string} sortDirection - Current sort direction: 'asc' | 'desc' | null
 */
export function AdminTableHeaderCell({ 
  children, 
  style = {}, 
  align = 'left', 
  scope = 'col',
  sortable = false,
  onSort,
  sortDirection = null
}) {
  const handleClick = () => {
    if (sortable && onSort) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(newDirection);
    }
  };

  return (
    <th
      style={{
        ...styles.headerCell,
        textAlign: align,
        ...(sortable && { cursor: 'pointer', userSelect: 'none' }),
        ...style,
      }}
      scope={scope}
      onClick={sortable ? handleClick : undefined}
      className={sortable ? 'admin-table-header-sortable' : ''}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
        {children}
        {sortable && (
          <span style={{ 
            fontSize: 'var(--font-size-xs)',
            color: sortDirection ? 'var(--color-brand-primary)' : 'var(--color-text-tertiary)',
            opacity: sortDirection ? 1 : 0.5
          }}>
            {sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '↕'}
          </span>
        )}
      </div>
    </th>
  );
}

// Styles removed - now using CSS classes from admin-ui.css
const styles = {
  row: {
    // Row styles handled by CSS
  },
  rowClickable: {
    cursor: 'pointer',
  },
};

